import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { UserRole } from '../types/gateway';
import styles from './AdminUserPanel.module.css';

/**
 * AdminUserPanel — modal that lists all users and lets admins add, delete,
 * email/copy invite links, reset passwords, and edit basic profile info.
 *
 * Wires to /api/auth/admin/* endpoints (JWT-gated, 403 for non-admins).
 */

interface AdminUserPanelProps {
  onClose: () => void;
}

interface AdminUser {
  username: string;
  name: string;
  email: string | null;
  role: string;
  uiRole: UserRole;
  createdAt?: number | string | null;
  lastLogin?: number | null;
}

function formatLastLogin(ts: number | null | undefined): string {
  if (!ts) return 'never';
  const now = Date.now() / 1000;
  const diff = now - ts;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

/** Roles offered when creating a new user. The first three map cleanly to the
 *  existing uiRole enum; `warehouse` and `operations` are extra OpenClaw roles
 *  the backend understands but which collapse onto a uiRole on the server. */
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'designer', label: 'Designer' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'operations', label: 'Operations' },
  { value: 'field', label: 'Field' },
] as const;

type RoleOption = typeof ROLE_OPTIONS[number]['value'];

const TOKEN_KEY = 'foreman-auth-token';

function authHeader(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY) || '';
  return { Authorization: `Bearer ${token}` };
}

async function readJsonOrThrow(res: Response): Promise<unknown> {
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data as { error?: string }).error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  // Fallback for non-secure contexts.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface ToastMsg {
  text: string;
  kind: 'ok' | 'err';
}

interface BuKeyStatus { has_key: boolean; last4: string | null }

export function AdminUserPanel({ onClose }: AdminUserPanelProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMsg | null>(null);

  // Per-row pending state so we can disable buttons on the row that's busy.
  const [pendingRow, setPendingRow] = useState<string | null>(null);

  // Reset-password popover.
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // Per-user Browser Use key status, populated lazily after user rows load.
  const [buKeys, setBuKeys] = useState<Record<string, BuKeyStatus>>({});

  // Set-key popover (admin-side; never shows the existing full key, only sets a new one).
  const [buTarget, setBuTarget] = useState<string | null>(null);
  const [buDraft, setBuDraft] = useState('');
  const [buSaving, setBuSaving] = useState(false);

  // Org-wide shared Browser Use key (fallback when a user has none of their own).
  const [orgBu, setOrgBu] = useState<BuKeyStatus | null>(null);
  const [orgBuLoading, setOrgBuLoading] = useState(true);
  const [orgBuOpen, setOrgBuOpen] = useState(false);
  const [orgBuDraft, setOrgBuDraft] = useState('');
  const [orgBuSaving, setOrgBuSaving] = useState(false);

  // Add-user form state.
  const [addOpen, setAddOpen] = useState(false);
  const [addUsername, setAddUsername] = useState('');
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState<RoleOption>('designer');
  const [addSendInvite, setAddSendInvite] = useState(true);
  const [addSaving, setAddSaving] = useState(false);
  const [addInviteUrl, setAddInviteUrl] = useState<string | null>(null);

  const showToast = useCallback((text: string, kind: 'ok' | 'err' = 'ok') => {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/admin/users', { headers: authHeader() });
      const data = (await readJsonOrThrow(res)) as { users: AdminUser[] };
      setUsers(data.users || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  // Lazily fetch each user's BU key status after the user list arrives. We
  // do these in parallel — there are only ~13 users on prod so the fan-out
  // is fine. Failures are non-fatal (key column just shows "—").
  useEffect(() => {
    if (users.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        users.map(async (u) => {
          try {
            const res = await fetch(
              `/api/auth/admin/users/${encodeURIComponent(u.username)}/browser-use-key`,
              { headers: authHeader() }
            );
            if (!res.ok) return [u.username, null] as const;
            const data = (await res.json()) as BuKeyStatus;
            return [u.username, data] as const;
          } catch {
            return [u.username, null] as const;
          }
        })
      );
      if (cancelled) return;
      const next: Record<string, BuKeyStatus> = {};
      for (const [u, s] of entries) {
        if (s) next[u] = s;
      }
      setBuKeys(next);
    })();
    return () => { cancelled = true; };
  }, [users]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Load the org-wide BU key status. Same shape as per-user (last4 + has_key).
  const refreshOrgBu = useCallback(async () => {
    setOrgBuLoading(true);
    try {
      const res = await fetch('/api/auth/admin/org/browser-use-key', { headers: authHeader() });
      if (!res.ok) {
        setOrgBu(null);
        return;
      }
      const data = (await res.json()) as BuKeyStatus;
      setOrgBu(data);
    } catch {
      setOrgBu(null);
    } finally {
      setOrgBuLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshOrgBu();
  }, [refreshOrgBu]);

  const handleOrgBuSave = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const key = orgBuDraft.trim();
      if (!key) {
        showToast('Paste a key first', 'err');
        return;
      }
      if (key.length < 4) {
        showToast('Key must be at least 4 chars', 'err');
        return;
      }
      setOrgBuSaving(true);
      try {
        const res = await fetch('/api/auth/admin/org/browser-use-key', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ key }),
        });
        const data = (await readJsonOrThrow(res)) as BuKeyStatus;
        setOrgBu(data);
        showToast('Org-wide BU key set', 'ok');
        setOrgBuOpen(false);
        setOrgBuDraft('');
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Save failed', 'err');
      } finally {
        setOrgBuSaving(false);
      }
    },
    [orgBuDraft, showToast]
  );

  const handleOrgBuClear = useCallback(async () => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      'Clear the org-wide Browser Use fallback key? Users without a personal key will run without a cloud key (Way-2 only).'
    );
    if (!ok) return;
    setOrgBuSaving(true);
    try {
      const res = await fetch('/api/auth/admin/org/browser-use-key', {
        method: 'DELETE',
        headers: authHeader(),
      });
      const data = (await readJsonOrThrow(res)) as BuKeyStatus;
      setOrgBu(data);
      showToast('Org-wide BU key cleared', 'ok');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Clear failed', 'err');
    } finally {
      setOrgBuSaving(false);
    }
  }, [showToast]);

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── Per-user actions ──────────────────────────────────────────────────────

  const handleResetSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!resetTarget || !resetPwd) return;
      setResetSaving(true);
      try {
        const res = await fetch(`/api/auth/admin/users/${encodeURIComponent(resetTarget)}/password`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ newPassword: resetPwd }),
        });
        await readJsonOrThrow(res);
        showToast(`Password updated for ${resetTarget}`, 'ok');
        setResetTarget(null);
        setResetPwd('');
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Reset failed', 'err');
      } finally {
        setResetSaving(false);
      }
    },
    [resetPwd, resetTarget, showToast]
  );

  const handleCopyInvite = useCallback(
    async (username: string) => {
      setPendingRow(username);
      try {
        const res = await fetch(`/api/auth/admin/users/${encodeURIComponent(username)}/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ sendEmail: false }),
        });
        const data = (await readJsonOrThrow(res)) as { inviteUrl: string };
        const ok = await copyToClipboard(data.inviteUrl);
        showToast(ok ? 'Invite link copied' : `Invite link: ${data.inviteUrl}`, ok ? 'ok' : 'err');
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Could not create invite', 'err');
      } finally {
        setPendingRow(null);
      }
    },
    [showToast]
  );

  const handleEmailInvite = useCallback(
    async (username: string, email: string | null) => {
      if (!email) {
        showToast(`No email on file for ${username}`, 'err');
        return;
      }
      setPendingRow(username);
      try {
        const res = await fetch(`/api/auth/admin/users/${encodeURIComponent(username)}/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ sendEmail: true }),
        });
        await readJsonOrThrow(res);
        showToast(`Invite emailed to ${email}`, 'ok');
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Could not send invite', 'err');
      } finally {
        setPendingRow(null);
      }
    },
    [showToast]
  );

  const handleDelete = useCallback(
    async (username: string) => {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`Delete user "${username}"? This cannot be undone.`);
      if (!ok) return;
      setPendingRow(username);
      try {
        const res = await fetch(`/api/auth/admin/users/${encodeURIComponent(username)}`, {
          method: 'DELETE',
          headers: authHeader(),
        });
        await readJsonOrThrow(res);
        showToast(`Deleted ${username}`, 'ok');
        await refresh();
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Delete failed', 'err');
      } finally {
        setPendingRow(null);
      }
    },
    [refresh, showToast]
  );

  // ── Browser Use API key (admin-side) ──────────────────────────────────────

  const handleBuSave = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!buTarget) return;
      const key = buDraft.trim();
      if (!key) {
        showToast('Paste a key first', 'err');
        return;
      }
      if (key.length < 4) {
        showToast('Key must be at least 4 chars', 'err');
        return;
      }
      setBuSaving(true);
      try {
        const res = await fetch(
          `/api/auth/admin/users/${encodeURIComponent(buTarget)}/browser-use-key`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...authHeader() },
            body: JSON.stringify({ key }),
          }
        );
        const data = (await readJsonOrThrow(res)) as BuKeyStatus;
        setBuKeys((prev) => ({ ...prev, [buTarget]: data }));
        showToast(`BU key set for ${buTarget}`, 'ok');
        setBuTarget(null);
        setBuDraft('');
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Save failed', 'err');
      } finally {
        setBuSaving(false);
      }
    },
    [buTarget, buDraft, showToast]
  );

  const handleBuClear = useCallback(
    async (username: string) => {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`Clear Browser Use key for @${username}?`);
      if (!ok) return;
      setPendingRow(username);
      try {
        const res = await fetch(
          `/api/auth/admin/users/${encodeURIComponent(username)}/browser-use-key`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...authHeader() },
            body: JSON.stringify({ key: '' }),
          }
        );
        const data = (await readJsonOrThrow(res)) as BuKeyStatus;
        setBuKeys((prev) => ({ ...prev, [username]: data }));
        showToast(`BU key cleared for ${username}`, 'ok');
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Clear failed', 'err');
      } finally {
        setPendingRow(null);
      }
    },
    [showToast]
  );

  // ── Add user ──────────────────────────────────────────────────────────────

  const resetAddForm = useCallback(() => {
    setAddUsername('');
    setAddName('');
    setAddEmail('');
    setAddRole('designer');
    setAddSendInvite(true);
    setAddInviteUrl(null);
  }, []);

  const handleAddSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setAddSaving(true);
      try {
        const res = await fetch('/api/auth/admin/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({
            username: addUsername,
            name: addName,
            email: addEmail || undefined,
            role: addRole,
            sendInvite: addSendInvite,
          }),
        });
        const data = (await readJsonOrThrow(res)) as { inviteUrl?: string };
        setAddInviteUrl(data.inviteUrl || null);
        showToast(
          addSendInvite ? `Created ${addUsername} and emailed invite` : `Created ${addUsername}`,
          'ok'
        );
        await refresh();
        // Don't auto-close — let admin copy the invite URL if they want.
      } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : 'Create failed', 'err');
      } finally {
        setAddSaving(false);
      }
    },
    [addUsername, addName, addEmail, addRole, addSendInvite, refresh, showToast]
  );

  const handleCopyAddInvite = useCallback(async () => {
    if (!addInviteUrl) return;
    const ok = await copyToClipboard(addInviteUrl);
    showToast(ok ? 'Invite link copied' : 'Could not copy — long-press to copy', ok ? 'ok' : 'err');
  }, [addInviteUrl, showToast]);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.username.localeCompare(b.username)),
    [users]
  );

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="User Management"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>User Management</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            {'\u2715'}
          </button>
        </div>

        <div className={styles.body}>
          <section className={styles.orgBuPanel}>
            <div className={styles.orgBuHeader}>
              <div>
                <div className={styles.orgBuTitle}>Org-wide Browser Use key (fallback)</div>
                <div className={styles.orgBuHelp}>
                  Used when a user has no personal key set. Spend gets attributed to whichever account this key belongs to.
                </div>
              </div>
              <div className={styles.orgBuStatus}>
                {orgBuLoading ? (
                  <span className={styles.dim}>{'Loading\u2026'}</span>
                ) : orgBu?.has_key && orgBu.last4 ? (
                  <span title="Org-wide Browser Use key on file">{`\u00B7\u00B7\u00B7\u00B7${orgBu.last4}`}</span>
                ) : (
                  <span className={styles.dim}>Not set</span>
                )}
              </div>
            </div>
            <div className={styles.orgBuActions}>
              <button
                type="button"
                className={styles.smallBtn}
                onClick={() => { setOrgBuOpen(true); setOrgBuDraft(''); }}
                disabled={orgBuLoading || orgBuSaving}
              >
                {orgBu?.has_key ? 'Replace' : 'Set'}
              </button>
              {orgBu?.has_key && (
                <button
                  type="button"
                  className={`${styles.smallBtn} ${styles.dangerBtn}`}
                  onClick={handleOrgBuClear}
                  disabled={orgBuLoading || orgBuSaving}
                >
                  Clear
                </button>
              )}
            </div>
          </section>

          <div className={styles.toolbar}>
            <span className={styles.count}>
              {loading ? 'Loading\u2026' : `${users.length} user${users.length === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => {
                if (addOpen) {
                  setAddOpen(false);
                  resetAddForm();
                } else {
                  setAddOpen(true);
                }
              }}
              disabled={loading}
            >
              {addOpen ? 'Cancel' : '+ Add User'}
            </button>
          </div>

          {addOpen && (
            <form className={styles.addForm} onSubmit={handleAddSubmit}>
              <div className={styles.formGrid}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Username</span>
                  <input
                    type="text"
                    required
                    pattern="^[a-z][a-z0-9_-]{1,30}$"
                    placeholder="lowercase, no spaces"
                    value={addUsername}
                    onChange={(e) => setAddUsername(e.target.value.toLowerCase())}
                    className={styles.input}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Full name</span>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Sam Designer"
                    value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                    className={styles.input}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Email</span>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                    className={styles.input}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Role</span>
                  <select
                    value={addRole}
                    onChange={(e) => setAddRole(e.target.value as RoleOption)}
                    className={styles.input}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={addSendInvite}
                  onChange={(e) => setAddSendInvite(e.target.checked)}
                />
                <span>Send invite email so they can set a password</span>
              </label>

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => {
                    setAddOpen(false);
                    resetAddForm();
                  }}
                  disabled={addSaving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={addSaving}>
                  {addSaving ? 'Creating\u2026' : 'Create user'}
                </button>
              </div>

              {addInviteUrl && (
                <div className={styles.inviteUrlRow}>
                  <code className={styles.inviteUrl}>{addInviteUrl}</code>
                  <button
                    type="button"
                    className={styles.smallBtn}
                    onClick={handleCopyAddInvite}
                  >
                    Copy
                  </button>
                </div>
              )}
            </form>
          )}

          {error && <div className={styles.err}>{error}</div>}

          {loading ? (
            <div className={styles.loading}>
              <span className={styles.spinner} aria-hidden /> Loading users{'\u2026'}
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Last login</th>
                    <th>BU key</th>
                    <th className={styles.actionsHead}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u) => {
                    const busy = pendingRow === u.username;
                    const bu = buKeys[u.username];
                    return (
                      <tr key={u.username}>
                        <td className={styles.userCell}>@{u.username}</td>
                        <td>{u.name}</td>
                        <td className={styles.emailCell}>{u.email || <span className={styles.dim}>—</span>}</td>
                        <td>
                          <span className={styles.roleChip} data-role={u.uiRole}>
                            {u.role}
                          </span>
                        </td>
                        <td
                          className={styles.userCell}
                          title={u.lastLogin ? new Date(u.lastLogin * 1000).toLocaleString() : 'No login recorded'}
                        >
                          {u.lastLogin ? formatLastLogin(u.lastLogin) : <span className={styles.dim}>never</span>}
                        </td>
                        <td className={styles.userCell}>
                          {bu?.has_key && bu.last4 ? (
                            <span title="Browser Use API key on file">{`\u00B7\u00B7\u00B7\u00B7${bu.last4}`}</span>
                          ) : (
                            <span className={styles.dim}>—</span>
                          )}
                        </td>
                        <td className={styles.actionsCell}>
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => {
                              setResetTarget(u.username);
                              setResetPwd('');
                            }}
                            disabled={busy}
                            title="Reset password"
                          >
                            Reset PW
                          </button>
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => { setBuTarget(u.username); setBuDraft(''); }}
                            disabled={busy}
                            title={bu?.has_key ? 'Replace Browser Use key' : 'Set Browser Use key'}
                          >
                            {bu?.has_key ? 'Replace BU' : 'Set BU'}
                          </button>
                          {bu?.has_key && (
                            <button
                              type="button"
                              className={`${styles.smallBtn} ${styles.dangerBtn}`}
                              onClick={() => handleBuClear(u.username)}
                              disabled={busy}
                              title="Clear Browser Use key"
                            >
                              Clear BU
                            </button>
                          )}
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => handleCopyInvite(u.username)}
                            disabled={busy}
                            title="Copy invite link to clipboard"
                          >
                            Copy Invite
                          </button>
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => handleEmailInvite(u.username, u.email)}
                            disabled={busy || !u.email}
                            title={u.email ? 'Email an invite link' : 'No email on file'}
                          >
                            Email Invite
                          </button>
                          <button
                            type="button"
                            className={`${styles.smallBtn} ${styles.dangerBtn}`}
                            onClick={() => handleDelete(u.username)}
                            disabled={busy}
                            title="Delete user"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {sortedUsers.length === 0 && (
                    <tr>
                      <td colSpan={6} className={styles.empty}>
                        No users yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {toast && (
          <div className={`${styles.toast} ${toast.kind === 'err' ? styles.toastErr : ''}`}>
            {toast.text}
          </div>
        )}

        {resetTarget && (
          <div className={styles.subBackdrop} onClick={() => !resetSaving && setResetTarget(null)}>
            <form
              className={styles.subModal}
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleResetSubmit}
            >
              <div className={styles.subTitle}>
                Reset password for <strong>@{resetTarget}</strong>
              </div>
              <input
                type="password"
                placeholder="New password (min 6 chars)"
                minLength={6}
                required
                autoFocus
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                className={styles.input}
              />
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setResetTarget(null)}
                  disabled={resetSaving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={resetSaving}>
                  {resetSaving ? 'Saving\u2026' : 'Save password'}
                </button>
              </div>
            </form>
          </div>
        )}

        {buTarget && (
          <div className={styles.subBackdrop} onClick={() => !buSaving && setBuTarget(null)}>
            <form
              className={styles.subModal}
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleBuSave}
            >
              <div className={styles.subTitle}>
                Set Browser Use key for <strong>@{buTarget}</strong>
              </div>
              <input
                type="text"
                placeholder="Paste bu_... key"
                required
                autoFocus
                spellCheck={false}
                autoComplete="off"
                value={buDraft}
                onChange={(e) => setBuDraft(e.target.value)}
                className={styles.input}
              />
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => { setBuTarget(null); setBuDraft(''); }}
                  disabled={buSaving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={buSaving || !buDraft.trim()}>
                  {buSaving ? 'Saving\u2026' : 'Save key'}
                </button>
              </div>
            </form>
          </div>
        )}

        {orgBuOpen && (
          <div className={styles.subBackdrop} onClick={() => !orgBuSaving && setOrgBuOpen(false)}>
            <form
              className={styles.subModal}
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleOrgBuSave}
            >
              <div className={styles.subTitle}>
                Set <strong>org-wide</strong> Browser Use fallback key
              </div>
              <input
                type="text"
                placeholder="Paste bu_... key"
                required
                autoFocus
                spellCheck={false}
                autoComplete="off"
                value={orgBuDraft}
                onChange={(e) => setOrgBuDraft(e.target.value)}
                className={styles.input}
              />
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => { setOrgBuOpen(false); setOrgBuDraft(''); }}
                  disabled={orgBuSaving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={orgBuSaving || !orgBuDraft.trim()}>
                  {orgBuSaving ? 'Saving\u2026' : 'Save key'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
